import React, { useId, type ChangeEvent, forwardRef } from 'react';

export interface UserInputProps {
  name: string;
  label: React.ReactNode;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  value?: string | number;
  defaultValue?: string | number;

  unit?: React.ReactNode;
  helpText?: string;
  placeholder?: string;
  disabled?: boolean;

  error?: string | null;
  id?: string;
}

export const UserInput = forwardRef<HTMLInputElement, UserInputProps>(function UserInput({ name, label, onChange, onBlur, value, defaultValue, unit, helpText, placeholder, disabled = false, error, id }, ref) {
  const autoId = useId();
  const inputId = id ?? name ?? `user-input-${autoId}`;

  const hasError = !!error;

  return (
    <div className="relative">
      {/* Hover area */}
      <div className="group grid w-full grid-cols-[3fr_2fr_1fr_4fr] gap-x-3 rounded-lg px-2 py-px transition-all h-max-20 bg-(--bg-color) focus-within:shadow-md">
        <label htmlFor={inputId} className="peer flex items-center text-sm font-medium hover:opacity-80 cursor-pointer transition-opacity" style={{ color: hasError ? 'var(--text-color-error)' : 'var(--text-color)' }}>
          {label}
        </label>

        <span className="flex items-center justify-center text-sm opacity-70" style={{ color: 'var(--text-color)' }}>
          {unit ? <>({unit})</> : null}
        </span>

        {/* This column stays for layout; no button now */}
        <div />
        <div className="relative flex items-center">
          <input
            id={inputId}
            name={name}
            ref={ref}
            type="text"
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            defaultValue={defaultValue}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full rounded-md border px-3 py-2 text-sm transition-all placeholder:opacity-50 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: disabled ? 'rgba(0,0,0,0.03)' : 'white',
              color: hasError ? 'var(--text-color-error)' : 'var(--text-color)',
              borderColor: hasError ? 'var(--text-color-error)' : 'rgba(0,0,0,0.15)',
              boxShadow: hasError ? '0 0 0 3px rgba(220, 38, 38, 0.1)' : 'none'
            }}
            title={error || undefined}
          />

          {hasError && (
            <div className="pointer-events-none absolute left-full top-1/2 z-1000 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium text-white shadow-lg" style={{ backgroundColor: 'var(--text-color-error)' }}>
              <div className="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent" style={{ borderRightColor: 'var(--text-color-error)' }} />
              {error}
            </div>
          )}
        </div>

        {/* Hover tooltip - shows only when label is hovered */}
        {helpText ? (
          <div
            className="pointer-events-none absolute left-0 top-full z-1000 mt-2 hidden min-w-50 max-w-75 rounded-lg p-3 text-sm leading-relaxed shadow-xl peer-hover:block"
            style={{
              backgroundColor: 'var(--bg-color)',
              color: 'var(--text-color)',
              border: '1px solid rgba(0,0,0,0.1)'
            }}
          >
            {helpText}
          </div>
        ) : null}
      </div>
    </div>
  );
});
